import axios from 'axios';

const axiosInstance = axios.create({
    baseURL: process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080'
});

axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

axiosInstance.interceptors.response.use(
    (response) => {
        // Sanitize response data to prevent undefined reference errors
        // This is especially important for banklink payments where card structures may be missing
        try {
            if (response?.data) {
                response.data = sanitizeResponseData(response.data);
            }
        } catch (sanitizeError) {
            console.warn('Error sanitizing response data:', sanitizeError);
            // Continue with original response data if sanitization fails
        }
        return response;
    },
    (error) => {
        // Sanitize error response data as well to prevent undefined reference errors
        try {
            if (error?.response?.data) {
                error.response.data = sanitizeResponseData(error.response.data);
            }
        } catch (sanitizeError) {
            console.warn('Error sanitizing error response data:', sanitizeError);
        }
        
        if (error.response?.status === 401 || error.response?.status === 403) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('username');
            localStorage.removeItem('userId');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

/**
 * Recursively sanitizes response data to ensure objects with reference properties are safely structured
 * Prevents "Cannot read properties of undefined (reading 'reference')" errors
 * Handles arrays, nested objects, and all edge cases for banklink payments
 * Uses a visited set to prevent infinite recursion on circular references
 */
function sanitizeResponseData(data, visited = new WeakSet()) {
    if (data === null || data === undefined) {
        return data;
    }
    
    // Handle arrays - recursively sanitize each item
    if (Array.isArray(data)) {
        return data.map(item => sanitizeResponseData(item, visited));
    }
    
    // Handle objects
    if (typeof data === 'object') {
        // Prevent infinite recursion on circular references
        if (visited.has(data)) {
            return data;
        }
        
        try {
            visited.add(data);
            const sanitized = { ...data };
            
            // Recursively sanitize all properties first (handles nested objects and arrays)
            for (const key in sanitized) {
                if (Object.prototype.hasOwnProperty.call(sanitized, key)) {
                    const value = sanitized[key];
                    // Remove null/undefined values that might be accessed with .reference
                    // This prevents "Cannot read properties of undefined (reading 'reference')" errors
                    if (value === null || value === undefined) {
                        // For properties that are commonly accessed with .reference, remove them entirely
                        // rather than leaving them as null/undefined
                        const mightBeAccessedWithReference = ['card', 'payment', 'order', 'transaction'].includes(key.toLowerCase());
                        if (mightBeAccessedWithReference) {
                            delete sanitized[key];
                        }
                    } else if (typeof value === 'object') {
                        // Recursively sanitize objects and arrays
                        sanitized[key] = sanitizeResponseData(value, visited);
                    }
                }
            }
            
            // Now ensure any object that might have a 'reference' property has it safely defined
            // Check common property names and also any object that might be accessed with .reference
            const commonReferenceProperties = ['card', 'payment', 'order', 'transaction', 'data', 'result'];
            for (const prop of commonReferenceProperties) {
                try {
                    const propValue = sanitized[prop];
                    if (propValue !== null && propValue !== undefined && typeof propValue === 'object' && !Array.isArray(propValue)) {
                        // Ensure the object has a reference property to prevent undefined access errors
                        if (!Object.prototype.hasOwnProperty.call(propValue, 'reference')) {
                            propValue.reference = null;
                        }
                    }
                } catch (e) {
                    // Skip if property access fails
                }
            }
            
            // Additionally, for deeply nested objects, ensure any object that might be accessed
            // with .reference has that property. This is especially important for payment/card structures
            // where code might access nested properties like data.card.reference
            for (const key in sanitized) {
                try {
                    const value = sanitized[key];
                    if (value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
                        // For objects that look like they might have reference properties (payment-related)
                        // or are commonly nested in payment responses, ensure reference exists
                        const looksLikeReferenceObject = commonReferenceProperties.includes(key.toLowerCase()) ||
                                                        key.toLowerCase().includes('card') ||
                                                        key.toLowerCase().includes('payment') ||
                                                        key.toLowerCase().includes('order') ||
                                                        key.toLowerCase().includes('transaction');
                        
                        if (looksLikeReferenceObject && !Object.prototype.hasOwnProperty.call(value, 'reference')) {
                            value.reference = null;
                        }
                    }
                } catch (e) {
                    // Skip if property access fails
                }
            }
            
            return sanitized;
        } catch (e) {
            console.warn('Error in sanitizeResponseData:', e);
            return data;
        }
    }
    
    return data;
}

export default axiosInstance;

